/**
 * Shared visual components for P2P transfer views.
 */

import { msg } from "@lingui/core/macro";
import type { Component } from "solid-js";
import type { TransferErrorKind } from "@boveda/shared";
import { useI18n } from "@/providers/i18n-provider";

export function TransferProgressBar(props: { percent: number }) {
  return (
    <div
      class="h-2 w-full overflow-hidden rounded-full bg-muted"
      role="progressbar"
      aria-valuenow={props.percent}
      aria-valuemin={0}
      aria-valuemax={100}
      data-testid="p2p-progress-bar"
    >
      <div
        class="h-full rounded-full bg-primary transition-[width] duration-200"
        style={{ width: `${Math.min(100, Math.max(0, props.percent))}%` }}
      />
    </div>
  );
}

export function TransferErrorCard(props: {
  kind: TransferErrorKind;
  icon: Component<{ class?: string }>;
}) {
  const { t } = useI18n();

  const message = () => {
    switch (props.kind) {
      case "expired": {
        return t(
          msg`This code is invalid or has expired. Codes only last a few minutes — generate a fresh one on the sending device.`,
        );
      }
      case "already-used": {
        return t(
          msg`This code has already been used. Codes are single-use — generate a fresh one on the sending device.`,
        );
      }
      case "invalid-code": {
        return t(msg`That transfer code doesn't look right. Check it and try again.`);
      }
      case "connection-failed": {
        return t(
          msg`The devices couldn't connect to each other. Keep both online (ideally on the same Wi-Fi) and try again.`,
        );
      }
      case "too-large": {
        return t(msg`There is too much data to transfer in one go.`);
      }
      case "network": {
        return t(msg`Network error. Check your connection and try again.`);
      }
      case "transfer-failed":
      default: {
        return t(
          msg`The transfer failed before completing. No partial data was imported — try again.`,
        );
      }
    }
  };

  return (
    <div
      class="flex items-start gap-3 rounded-xl border border-destructive/20 bg-destructive/5 p-4"
      data-testid="p2p-error-message"
    >
      <props.icon class="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
      <p class="text-sm text-destructive">{message()}</p>
    </div>
  );
}
